import { asStore } from '@src/application/store/as-store.ts';
import { computed, ref } from 'vue';
import { FavoriteResponse } from '@src/api/interface/response/favorite-response.ts';
import apiClient from '@src/api/api-client.ts';
import { useRouterComposable } from '@router/router-composable.ts';
import { EditFavoriteRequest } from '@src/api/interface/request/edit-favorite-request.ts';
import { RouteDefinition } from '@router/interfaces/route-definition.ts';

export default function useCurrentUserFavoriteComposable() {
    const favorites = asStore('favorites', () => ref<Array<FavoriteResponse>|null>(null), false);

    async function loadFavoritesByCurrentUser() {
        favorites.value = (await apiClient.get('/be/favorite/favorites-by-current-user')).data;
    }

    const { currentRoute, getRouteByRouteName } = useRouterComposable();

    const canAddToFavorites = computed<boolean>(() => {
        return currentRoute.value.meta.favoriteRouteName !== undefined || currentRoute.value.props === undefined;
    });

    function getRouteForFavorites() {
        let route: RouteDefinition = currentRoute.value;
        if (currentRoute.value.meta.favoriteRouteName !== undefined) {
            const favoriteRoute = getRouteByRouteName(currentRoute.value.meta.favoriteRouteName);
            if (favoriteRoute !== null) {
                route = favoriteRoute;
            }
        }

        return route;
    }

    async function addCurrentRouteToFavorites() {
        const route = getRouteForFavorites();
        const createdFavorite = (await apiClient.post('/be/favorite', {
            routeName: route.name,
            title: route.meta.title,
        })).data;
        if (favorites.value === null) {
            favorites.value = [];
        }

        favorites.value.push(createdFavorite);
    }

    async function removeCurrentRouteFromFavorites() {
        const favoriteByCurrentRoute = getFavoriteByCurrentRoute();
        if (favoriteByCurrentRoute === null) {
            throw new Error('Zur aktuellen Route konnte kein Favorite gefunden werden.');
        }
        await removeFavorites([favoriteByCurrentRoute]);
    }

    async function removeFavorites(favoritesToRemove: Array<FavoriteResponse>) {
        if (favorites.value === null) {
            throw Error('Es sind keine Favoriten verfügbar.');
        }
        const ids = favoritesToRemove.map((favoriteToDelete: FavoriteResponse) => favoriteToDelete.id);
        await apiClient.delete('/be/favorite/favorites', { ids: ids });
        for (const favoriteToRemove of favoritesToRemove) {
            const index = favorites.value.indexOf(favoriteToRemove);
            favorites.value.splice(index, 1);
        }
    }

    async function editFavorites(editFavoritesRequest: Array<EditFavoriteRequest>) {
        favorites.value = (await apiClient.patch('/be/favorite/favorites', editFavoritesRequest)).data;
    }

    const isCurrentRouteFavored = computed<boolean>(() => {
        return getFavoriteByCurrentRoute() !== null;
    });

    function getFavoriteByCurrentRoute(): FavoriteResponse|null {
        if (favorites.value === null) {
            return null;
        }

        const route = getRouteForFavorites();
        for (const favorite of favorites.value) {
            if (favorite.routeName === route.name) {
                return favorite;
            }
        }

        return null;
    }

    return {
        loadFavoritesByCurrentUser,
        addCurrentRouteToFavorites,
        removeCurrentRouteFromFavorites,
        editFavorites,
        removeFavorites,
        isCurrentRouteFavored,
        canAddToFavorites,
        favorites,
    };
}
